import React, { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence, MotionConfig, useReducedMotion } from "framer-motion";
import { ChevronLeft, Volume2, ArrowRight, Check, Star, BookOpen, Loader2 } from "lucide-react";
import MobileLayout from "@/components/MobileLayout";
import { sfx } from "@/lib/sfx";
import AvatarTeacher from "@/components/language/AvatarTeacher";
import { LanguageJourney } from '@/components/language/LanguageJourney';
import { LanguagePractice } from '@/components/language/LanguagePractice';
import { lessonExercises, LESSON_PASS_SCORE, languageLessonId, type PracticeResult, type PracticeSelectionOptions } from '@/lib/language/practice';
import SpeakingPhase from "@/components/language/SpeakingPhase";
import { useLanguage } from "@/i18n/LanguageProvider";
import { useLanguageLearning, type LangCode } from "@/hooks/useLanguageLearning";
import { useLanguageLesson } from "@/hooks/useLanguageLesson";
import { useLanguageReact } from "@/hooks/useLanguageReact";
import ReportContentButton from "@/components/report/ReportContentButton";
import { REPORT_SURFACES, languageLessonContentId } from "@/lib/report/surfaces";
import { useReportedContent } from "@/contexts/ReportedContentContext";
import { useLanguageTTS } from "@/hooks/useLanguageTTS";
import { useXP } from "@/hooks/useXP";
import { LanguageReview } from "@/components/language/LanguageReview";
import { LanguageState } from "@/components/language/LanguageState";
import { beginLanguageLesson, finishLanguageLesson } from "@/lib/language/records";
import { createFitnessId as createLanguageId } from "@/lib/fitnessId";
import { notifyLanguageChanged } from "@/hooks/useLanguageResource";
import { normalizeNativeLang, type ResolvedLesson } from "@/data/languageLessons";

/* ─── Constants ─── */
const LANG_META: Record<string, { emoji: string; label: string }> = {
  en: { emoji: "🇬🇧", label: "English" }, pt: { emoji: "🇧🇷", label: "Português" },
  nl: { emoji: "🇳🇱", label: "Nederlands" }, es: { emoji: "🇪🇸", label: "Español" },
  fr: { emoji: "🇫🇷", label: "Français" }, de: { emoji: "🇩🇪", label: "Deutsch" },
  it: { emoji: "🇮🇹", label: "Italiano" }, ja: { emoji: "🇯🇵", label: "日本語" },
};
const LEVEL_LABELS: Record<string, string> = { beginner: "A1-A2", intermediate: "B1-B2", advanced: "C1-C2" };
const XP_PER_LESSON = 15;

/* ─── Particles ─── */
const PARTICLES = [
  { left: "12%", top: "18%", w: 2, color: "#00e5ff", dur: 10, yD: -14, xD: 5 },
  { left: "78%", top: "30%", w: 1.5, color: "hsl(265 60% 65%)", dur: 12, yD: -10, xD: -4 },
  { left: "45%", top: "55%", w: 2.5, color: "#00e5ff", dur: 9, yD: -16, xD: 3 },
  { left: "28%", top: "72%", w: 1.8, color: "hsl(265 60% 65%)", dur: 11, yD: -12, xD: -6 },
];

/* ─── Phase colors ─── */
const PHASE_COLORS: Record<string, { border: string; glow: string; accent: string }> = {
  vocabulary: { border: "hsl(187 72% 50% / 0.25)", glow: "hsl(187 72% 50% / 0.06)", accent: "hsl(187 72% 55%)" },
  phrases: { border: "hsl(265 60% 55% / 0.25)", glow: "hsl(265 60% 55% / 0.06)", accent: "hsl(265 60% 65%)" },
  expressions: { border: "hsl(38 92% 55% / 0.25)", glow: "hsl(38 92% 55% / 0.06)", accent: "hsl(38 92% 60%)" },
  speaking: { border: "hsl(187 72% 50% / 0.30)", glow: "hsl(187 72% 50% / 0.08)", accent: "hsl(187 72% 60%)" },
};

/* ─── Confetti stars config ─── */
const CONFETTI_STARS = [
  { left: "15%", delay: 0, dur: 2.2, rot: 120 },
  { left: "45%", delay: 0.3, dur: 1.8, rot: -90 },
  { left: "70%", delay: 0.6, dur: 2.0, rot: 200 },
  { left: "85%", delay: 0.15, dur: 2.4, rot: -150 },
];

/* ─── Types ─── */
type Phase = "lessons" | "active" | "complete";
type CardPhase = "vocabulary" | "phrases" | "expressions" | "practice" | "speaking";
const stagger = (i: number) => ({ delay: 0.06 + i * 0.07, duration: 0.5 });

/* ═══ COMPONENT ═══ */
const LanguageLessonContent: React.FC<{ learning: ReturnType<typeof useLanguageLearning> }> = ({ learning }) => {
  const reduceMotion = useReducedMotion();
  const { langCode } = useParams<{ langCode: string }>();
  const navigate = useNavigate();
  const { t, lang } = useLanguage();
  const { profiles, addVocabulary } = learning;
  const profile = profiles.find(p => p.learning_language === langCode) ?? null;
  const { fetchLesson } = useLanguageLesson();
  const mirrorReact = useLanguageReact();
  const { isReported } = useReportedContent();
  // Identidade da reacao na tela: a sessao de idioma que a gerou.
  const [reactedSessionId, setReactedSessionId] = useState<string | null>(null);
  const tts = useLanguageTTS();
  const { addXP } = useXP();

  const meta = LANG_META[langCode || "en"] || LANG_META.en;
  const level = profile ? (LEVEL_LABELS[profile.level] || profile.level) : "";

  const [lessons, setLessons] = useState<ResolvedLesson[]>([]);
  // lessonsKey tracks which (langCode, nativeLang) combo `lessons` was
  // generated for. Used by the load-effect guard to decide whether a
  // refetch is needed after a lang/UI-lang switch.
  const [lessonsKey, setLessonsKey] = useState<string | null>(null);
  const [lessonsLoading, setLessonsLoading] = useState(false);
  const [lessonsError, setLessonsError] = useState<string | null>(null);

  const [phase, setPhase] = useState<Phase>("lessons");
  const [activeLesson, setActiveLesson] = useState<ResolvedLesson | null>(null);
  const [cardPhase, setCardPhase] = useState<CardPhase>("vocabulary");
  const [cardIndex, setCardIndex] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [completedLessons, setCompletedLessons] = useState<Set<string>>(() => new Set());
  const [audioPulse, setAudioPulse] = useState(false);
  const lessonStartRef = useRef<number>(0);
  const isSubmittingRef = useRef<boolean>(false);
  // sessionIdRef: shared between SpeakingPhase (as session_id on each attempt)
  // and the language_sessions INSERT at completion, so speaking attempts can
  // be joined back to the parent session row.
  const sessionIdRef = useRef<string>("");
  const mounted = useRef(false), transitionBusy = useRef(false);
  const completionDuration = useRef<number | null>(null), completionConfirmed = useRef(false);
  const [busy, setBusy] = useState(false), [saveError, setSaveError] = useState(false), [awardedXP, setAwardedXP] = useState(0);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [practiceResult, setPracticeResult] = useState<PracticeResult | null>(null);
  const createExercises = useCallback((round: number, options?: PracticeSelectionOptions) => activeLesson ? lessonExercises(activeLesson, round, options) : [], [activeLesson]);
  const fetchLessonRef = useRef(fetchLesson); fetchLessonRef.current = fetchLesson;
  const ttsRef = useRef(tts); ttsRef.current = tts;
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; ttsRef.current.cancel(); }; }, []);
  const capture = useCallback(() => { const current = learning.capture(); return () => { if (!mounted.current || !current()) throw new Error("Language account changed"); }; }, [learning.capture]);
  const userLevel = profile?.level ?? "beginner";
  const lessonIdFor = useCallback((index: number) => languageLessonId(langCode!, userLevel, index), [langCode, userLevel]);

  useEffect(() => {
    if (!langCode || !profile) return;
    let cancelled = false;
    const nativeLang = profile.native_language, key = `${langCode}:${nativeLang}:${userLevel}`;
    setLessonsLoading(true); setLessonsError(null);
    const retained = lessonsKey === key ? lessons : [];
    if (lessonsKey !== key) setLessons([]);
    Promise.all(Array.from({ length: 10 }, (_, index) => index + 1).map(async index => {
      const existing = retained.find(lesson => lesson.level === index); if (existing) return existing;
      const result = await fetchLessonRef.current({ native_lang: nativeLang, learning_lang: langCode, level: userLevel, lesson_index: index });
      return result ? { ...result, id: lessonIdFor(index), level: index } as ResolvedLesson : null;
    })).then(result => {
      if (cancelled) return;
      const valid = result.filter((item): item is ResolvedLesson => item !== null);
      setLessons(valid); setLessonsKey(key); setLessonsLoading(false); if (valid.length < 10) setLessonsError("partial");
    }).catch(() => { if (!cancelled) { setLessonsLoading(false); setLessonsError("failed"); } });
    return () => { cancelled = true; };
    // The retry reuses successful lessons; changing account/target/level remounts this component.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [langCode, lang, userLevel, loadAttempt]);
  useEffect(() => {
    setCompletedLessons(new Set(learning.sessions.filter(row => row.language === langCode && row.session_type === "lesson" && row.duration_seconds > 0 && row.lesson_id).map(row => row.lesson_id!)));
  }, [learning.sessions, langCode]);

  const handleStartLesson = useCallback(async (lesson: ResolvedLesson) => {
    if (transitionBusy.current || isSubmittingRef.current) return;
    const assert = capture(); transitionBusy.current = true; setBusy(true); setSaveError(false);
    if (activeLesson?.id !== lesson.id || !sessionIdRef.current) sessionIdRef.current = createLanguageId();
    setActiveLesson(lesson);
    try {
      await beginLanguageLesson(learning.userId!, sessionIdRef.current, langCode!, lesson.id, assert); assert();
      lessonStartRef.current = Date.now(); completionDuration.current = null; completionConfirmed.current = false; setAwardedXP(0); setPracticeResult(null);
      setCardPhase("vocabulary"); setCardIndex(0); setFlipped(false); setPhase("active");
    } catch { if (mounted.current) setSaveError(true); }
    finally { transitionBusy.current = false; if (mounted.current) setBusy(false); }
  }, [activeLesson, capture, learning.userId, langCode]);

  // Progress bar accounts for 4 phases: vocab, phrases, expressions, +1 for speaking.
  const totalCards = activeLesson
    ? activeLesson.vocabulary.length + activeLesson.phrases.length + activeLesson.expressions.length + 2
    : 0;
  const currentGlobalIndex = activeLesson ? (() => {
    if (cardPhase === "vocabulary") return cardIndex;
    if (cardPhase === "phrases") return activeLesson.vocabulary.length + cardIndex;
    if (cardPhase === "expressions") return activeLesson.vocabulary.length + activeLesson.phrases.length + cardIndex;
    // speaking is the single final "card"
    return activeLesson.vocabulary.length + activeLesson.phrases.length + activeLesson.expressions.length + (cardPhase === "speaking" ? 1 : 0);
  })() : 0;

  const buildPhaseVocab = useCallback((lesson: ResolvedLesson, cp: CardPhase) => {
    const lang = (langCode || "en") as LangCode;
    if (cp === "vocabulary") {
      return lesson.vocabulary.map(v => ({ language: lang, word: v.word, translation: v.translation, lesson_id: lesson.id }));
    }
    if (cp === "phrases") {
      return lesson.phrases.map(p => ({ language: lang, word: p.phrase, translation: p.translation, lesson_id: lesson.id }));
    }
    return lesson.expressions.map(e => ({ language: lang, word: e.expression, translation: e.meaning, lesson_id: lesson.id }));
  }, [langCode]);

  const handleNext = useCallback(async () => {
    if (!activeLesson || transitionBusy.current || isSubmittingRef.current) return;
    const assert = capture(), phaseItems = cardPhase === "vocabulary" ? activeLesson.vocabulary : cardPhase === "phrases" ? activeLesson.phrases : activeLesson.expressions;
    setSaveError(false);
    if (cardIndex < phaseItems.length - 1) { setCardIndex(index => index + 1); setFlipped(false); return; }
    transitionBusy.current = true; setBusy(true);
    try { await addVocabulary(buildPhaseVocab(activeLesson, cardPhase)); assert(); setFlipped(false); setCardIndex(0); setCardPhase(cardPhase === "vocabulary" ? "phrases" : cardPhase === "phrases" ? "expressions" : "practice"); }
    catch { if (mounted.current) setSaveError(true); }
    finally { transitionBusy.current = false; if (mounted.current) setBusy(false); }
  }, [activeLesson, cardPhase, cardIndex, capture, addVocabulary, buildPhaseVocab]);

  const completeLesson = useCallback(async () => {
    if (!practiceResult || practiceResult.score < LESSON_PASS_SCORE || !activeLesson || !langCode || isSubmittingRef.current || completionConfirmed.current) return;
    const assert = capture(); isSubmittingRef.current = true; setBusy(true); setSaveError(false);
    completionDuration.current ??= Math.max(1, Math.floor((Date.now() - lessonStartRef.current) / 1000));
    try {
      await finishLanguageLesson(learning.userId!, sessionIdRef.current, completionDuration.current, [...activeLesson.vocabulary.map(v => v.word), ...activeLesson.phrases.map(p => p.phrase), ...activeLesson.expressions.map(e => e.expression)], assert, practiceResult.score); assert();
      completionConfirmed.current = true; setCompletedLessons(previous => new Set([...previous, activeLesson.id]));
      setPhase("complete"); sfx.success("module"); notifyLanguageChanged();
      // Optional reactions and rewards cannot delay or invalidate the saved result.
      void learning.markPracticedToday().catch(() => {});
      void addXP("language", "lesson_completed", XP_PER_LESSON, sessionIdRef.current).then(result => { try { assert(); if (result.inserted) setAwardedXP(XP_PER_LESSON); } catch { /* Previous owner. */ } }).catch(() => {});
      setReactedSessionId(sessionIdRef.current);
      void mirrorReact.react({ languageSessionId: sessionIdRef.current, nativeLang: normalizeNativeLang(lang), learningLang: langCode, lessonId: activeLesson.id });
    } catch { if (mounted.current) setSaveError(true); }
    finally { isSubmittingRef.current = false; if (mounted.current) setBusy(false); }
  }, [practiceResult, activeLesson, langCode, capture, learning, addXP, mirrorReact, lang]);

  const handleBackToLessons = useCallback(() => {
    if (transitionBusy.current || isSubmittingRef.current) return;
    tts.cancel(); mirrorReact.clear();
    setSaveError(false);
    setPhase("lessons");
    setActiveLesson(null);
  }, [mirrorReact, tts]);

  const currentCard = useMemo(() => {
    if (!activeLesson) return null;
    if (cardPhase === "vocabulary") { const v = activeLesson.vocabulary[cardIndex]; return v ? { front: v.word, back: v.translation, extra: v.example, type: "vocabulary" as const } : null; }
    if (cardPhase === "phrases") { const p = activeLesson.phrases[cardIndex]; return p ? { front: p.phrase, back: p.translation, extra: p.context, type: "phrase" as const } : null; }
    const e = activeLesson.expressions[cardIndex]; return e ? { front: e.expression, back: e.meaning, extra: e.literal, type: "expression" as const } : null;
  }, [activeLesson, cardPhase, cardIndex]);

  const phaseStyle = PHASE_COLORS[cardPhase] || PHASE_COLORS.vocabulary;

  /* ══ COMPLETION SCREEN ══ */
  if (phase === "complete" && activeLesson) {
    return (
      <MobileLayout className="language-page pb-28 pt-8 flex flex-col items-center justify-center px-6 relative overflow-hidden">
        {/* Confetti stars */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          {CONFETTI_STARS.map((s, i) => (
            <motion.div key={i} className="absolute"
              style={{ left: s.left, top: -20 }}
              initial={{ y: -20, opacity: 1, rotate: 0 }}
              animate={reduceMotion ? undefined : { y: [- 20, 400], opacity: [1, 1, 0], rotate: [0, s.rot] }}
              transition={{ duration: s.dur, delay: s.delay, repeat: Infinity, ease: "easeIn" }}>
              <Star size={16} style={{ color: i % 2 === 0 ? "hsl(187 72% 55%)" : "hsl(38 92% 55%)", opacity: 0.6 }} />
            </motion.div>
          ))}
          {/* Breathing orb */}
          <motion.div className="absolute w-[200px] h-[200px] rounded-full"
            style={{ top: "33%", left: "50%", marginLeft: -100, background: "radial-gradient(circle, rgba(0,229,255,0.05) 0%, transparent 70%)", willChange: "transform, opacity" }}
            animate={reduceMotion ? undefined : { opacity: [0.3, 0.6, 0.3], scale: [0.9, 1.1, 0.9] }}
            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }} />
        </div>

        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="w-full max-w-sm text-center relative" style={{ zIndex: 1 }}>
          <motion.div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
            style={{ background: "hsl(140 50% 35% / 0.2)", border: "2px solid hsl(140 60% 50% / 0.4)", boxShadow: "0 0 30px hsl(140 60% 50% / 0.15)" }}
            animate={reduceMotion ? undefined : { scale: [1, 1.08, 1] }} transition={{ duration: 1.5, repeat: Infinity }}>
            <Check size={28} style={{ color: "hsl(140 60% 55%)" }} />
          </motion.div>
          <p className="font-display text-[22px] font-bold text-foreground mb-1">{t("language.lessonView.complete")}</p>
          <p className="text-[13px] font-body text-foreground/50 mb-6">{activeLesson.title}</p>
          <div className="grid grid-cols-2 gap-3 mb-6">
            <div className="rounded-xl p-4 text-center" style={{ background: "hsl(187 72% 50% / 0.06)", border: "1px solid hsl(187 72% 50% / 0.15)" }}>
              <p className="font-display text-[20px] font-bold text-foreground">{practiceResult?.score}%</p>
              <p className="text-[9px] font-body" style={{ color: "hsl(218 14% 50%)" }}>{t("language.game.roundScore")}</p>
            </div>
            <div className="rounded-xl p-4 text-center" style={{ background: "hsl(187 72% 50% / 0.06)", border: "1px solid hsl(187 72% 50% / 0.15)" }}>
              <p className="font-display text-[20px] font-bold" style={{ color: "hsl(187 72% 55%)" }}>{awardedXP > 0 ? `+${awardedXP}` : "—"}</p>
              <p className="text-[9px] font-body" style={{ color: "hsl(218 14% 50%)" }}>XP</p>
            </div>
          </div>

          {(mirrorReact.isLoading || mirrorReact.reaction) &&
           !isReported(REPORT_SURFACES.REACTION_LANGUAGE, reactedSessionId) && (
            <div
              className="rounded-2xl text-left mb-4"
              style={{
                background: "hsl(var(--card) / 0.7)",
                border: "1px solid hsl(187 72% 50% / 0.25)",
                boxShadow: "0 0 24px hsl(187 72% 50% / 0.08)",
                backdropFilter: "blur(12px)",
                padding: 16,
              }}
            >
              <p
                className="font-display tracking-[0.18em] uppercase text-[9px] mb-2"
                style={{ color: "hsl(187 72% 55%)", opacity: 0.7 }}
              >
                MIRROR
              </p>
              {mirrorReact.isLoading ? (
                <div className="space-y-2">
                  <div className="h-3 w-full rounded animate-pulse" style={{ background: "hsl(187 72% 50% / 0.12)" }} />
                  <div className="h-3 w-5/6 rounded animate-pulse" style={{ background: "hsl(187 72% 50% / 0.10)" }} />
                  <div className="h-3 w-3/5 rounded animate-pulse" style={{ background: "hsl(187 72% 50% / 0.08)" }} />
                </div>
              ) : mirrorReact.reaction ? (
                <>
                  <p className="font-body text-[14px] text-foreground/85 leading-relaxed">
                    {mirrorReact.reaction}
                  </p>
                  <div className="flex justify-end items-center gap-1 mt-2">
                    {/* Denuncia da reacao do Espelho a licao concluida. */}
                    {reactedSessionId && mirrorReact.reaction && (
                      <ReportContentButton
                        surface={REPORT_SURFACES.REACTION_LANGUAGE}
                        contentId={reactedSessionId}
                        snapshot={mirrorReact.reaction}
                        context={{ module: "language", learning_lang: langCode }}
                      />
                    )}
                    <button
                      onClick={() => {
                        tts.speak(mirrorReact.reaction!, normalizeNativeLang(lang)).catch(() => {
                          /* error surfaced via tts.error */
                        });
                      }}
                      disabled={tts.isLoading}
                      className="w-7 h-7 rounded-full flex items-center justify-center active:scale-90 disabled:opacity-60"
                      style={{
                        background: "hsl(187 72% 50% / 0.10)",
                        border: "1px solid hsl(187 72% 50% / 0.25)",
                      }}
                      aria-label={t("common.listen")}
                    >
                      {tts.isLoading ? (
                        <Loader2 size={12} className="animate-spin" style={{ color: "hsl(187 72% 55%)" }} />
                      ) : (
                        <Volume2 size={12} style={{ color: "hsl(187 72% 55%)" }} />
                      )}
                    </button>
                  </div>
                  {tts.error && (
                    <p className="text-[10px] font-body text-foreground/55 mt-1 text-right">
                      {t("language.lessonView.audioError")}
                    </p>
                  )}
                </>
              ) : null}
            </div>
          )}

          <section className="language-status text-left"><h2 className="font-semibold">{t('language.game.afterLesson')}</h2><p>{t('language.game.afterLessonHint')}</p><div className="language-next-actions"><button onClick={() => navigate(`/language/${langCode}?practice=review`)}>{t('language.game.homework')}</button><button onClick={() => navigate(`/language/live?lang=${langCode}`)}>{t('language.game.talkTeacher')}</button></div></section>
          <button disabled={busy} onClick={handleBackToLessons}
            className="w-full py-3.5 rounded-xl font-display text-[14px] font-semibold active:scale-[0.97]"
            style={{ background: "hsl(187 72% 50%)", color: "#0a0a0f" }}>
            {t("language.lessonView.backToLessons")}
          </button>
        </motion.div>
      </MobileLayout>
    );
  }

  /* ══ ACTIVE LESSON (FLASHCARDS + SPEAKING) ══ */
  // Speaking phase requires sessionIdRef.current — that ref is set
  // synchronously in handleStartLesson before the parent INSERT awaits, so
  // it's normally truthy by the time React reaches this line. The guard is
  // belt-and-suspenders against any future code path that might bypass
  // handleStartLesson and try to mount SpeakingPhase directly.
  const isPracticePhase = phase === "active" && cardPhase === "practice";
  const isSpeakingPhase =
    phase === "active" && !!activeLesson && cardPhase === "speaking" && !!sessionIdRef.current;
  const speakingTarget = activeLesson
    ? (activeLesson.phrases[0]?.phrase || activeLesson.expressions[0]?.expression || activeLesson.vocabulary[0]?.word || "")
    : "";
  if (phase === "active" && activeLesson && (currentCard || isSpeakingPhase || isPracticePhase)) {
    const progressPct = totalCards > 0 ? Math.round(((currentGlobalIndex + 1) / totalCards) * 100) : 0;
    const phaseLabel = cardPhase === "vocabulary" ? t("language.lessonView.vocabulary")
      : cardPhase === "phrases" ? t("language.lessonView.phrases")
      : cardPhase === "expressions" ? t("language.lessonView.expressions")
      : isPracticePhase ? t("language.game.practice") : t("language.speaking.title");

    return (
      <MobileLayout className="language-page pb-28 pt-6 flex flex-col relative overflow-hidden" noPadding>
        {/* Floating particles */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none" style={{ zIndex: 0 }}>
          {PARTICLES.map((p, i) => (
            <motion.div key={i} className="absolute rounded-full"
              style={{ left: p.left, top: p.top, width: p.w, height: p.w, background: p.color, opacity: 0.15, willChange: "transform, opacity" }}
              animate={reduceMotion ? undefined : { y: [0, p.yD, 0], x: [0, p.xD, 0], opacity: [0.12, 0.18, 0.12] }}
              transition={{ duration: p.dur, repeat: Infinity, ease: "easeInOut", delay: i * 0.4 }} />
          ))}
        </div>

        <div className="px-5 flex-1 flex flex-col relative" style={{ zIndex: 1 }}>
          {/* Header */}
          <div className="flex items-center justify-between mb-3">
          <button disabled={busy} onClick={handleBackToLessons} className="flex items-center gap-1 text-muted-foreground active:opacity-70">
              <ChevronLeft size={16} /><span className="text-[13px] font-display">{t("common.back")}</span>
            </button>
            <div className="flex items-center gap-1">
              <p className="text-[11px] font-display font-medium text-foreground/60">{currentGlobalIndex + 1} / {totalCards}</p>
              {/* Denuncia da LICAO gerada. O conteudo aqui (vocabulario,
                  frases, expressoes) sai do language-lesson via Claude e fica
                  em language_lessons_cache, que e lido por TODOS os usuarios
                  (cache publico) — uma licao ruim nao afeta so quem viu.
                  Identidade = a chave do cache, nao um uuid: a resposta do edge
                  fn nao devolve id de linha. */}
              <ReportContentButton
                surface={REPORT_SURFACES.LANGUAGE_LESSON}
                contentId={languageLessonContentId({
                  nativeLang: profile?.native_language || normalizeNativeLang(lang),
                  learningLang: langCode || "",
                  // MESMA expressao do fetch (linhas 133-134): se divergir, o
                  // content_id da denuncia nao bate com a licao que foi gerada
                  // e o corte esconderia a licao errada.
                  level: profiles.find((p) => p.learning_language === langCode)?.level || "beginner",
                  lessonIndex: activeLesson.level,
                })}
                snapshot={JSON.stringify({
                  title: activeLesson.title,
                  description: activeLesson.description,
                  vocabulary: activeLesson.vocabulary,
                  phrases: activeLesson.phrases,
                  expressions: activeLesson.expressions,
                })}
                snapshotKind="json"
                context={{ lesson_id: activeLesson.id, learning_lang: langCode }}
              />
            </div>
          </div>

          {/* Progress bar */}
          <div className="w-full h-1.5 rounded-full mb-4 overflow-hidden" style={{ background: "hsl(187 72% 50% / 0.08)" }}>
            <motion.div className="h-full rounded-full" animate={{ width: `${progressPct}%` }}
              transition={{ duration: 0.4 }} style={{ background: phaseStyle.accent }} />
          </div>

          {/* Phase label */}
          <p className="text-[9px] font-display tracking-[0.18em] uppercase text-center mb-1" style={{ color: phaseStyle.accent, opacity: 0.7 }}>
            {phaseLabel}
          </p>

          {/* Flashcard OR Speaking phase */}
          <div className="flex-1 flex flex-col items-center justify-center">
            {isPracticePhase ? <LanguagePractice createExercises={createExercises} language={langCode || 'en'} onComplete={async result => { setPracticeResult(result); setCardPhase('speaking'); }} /> : isSpeakingPhase ? (
              <SpeakingPhase
                disabled={busy}
                targetText={speakingTarget}
                language={langCode || "en"}
                sessionId={sessionIdRef.current}
                onComplete={() => { void completeLesson(); }}
              />
            ) : (
              <AnimatePresence mode="wait">
                <motion.div key={`${cardPhase}-${cardIndex}`}
                  initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -40 }}
                  transition={{ duration: 0.25 }} className="w-full">
                  <button onClick={() => setFlipped(f => !f)}
                    className="w-full rounded-2xl p-6 text-center min-h-[200px] flex flex-col items-center justify-center transition-all"
                    style={{
                      background: "hsl(var(--card) / 0.7)",
                      border: `1.5px solid ${flipped ? "hsl(140 60% 50% / 0.25)" : phaseStyle.border}`,
                      boxShadow: `0 0 30px ${phaseStyle.glow}`,
                      backdropFilter: "blur(12px)",
                    }}>
                    {!flipped ? (
                      <>
                        <p className="font-display text-[24px] font-bold text-foreground leading-snug mb-3">{currentCard!.front}</p>
                        <p className="text-[11px] font-body text-foreground/35">{t("language.lessonView.tapToFlip")}</p>
                      </>
                    ) : (
                      <>
                        <p className="font-display text-[22px] font-bold mb-2" style={{ color: "hsl(140 60% 60%)" }}>{currentCard!.back}</p>
                        <p className="text-[12px] font-body text-foreground/50 italic leading-relaxed">{currentCard!.extra}</p>
                      </>
                    )}
                  </button>

                  {/* Audio button with pulse */}
                  <div className="flex flex-col items-center mt-3">
                    <motion.button
                      onClick={(e) => {
                        e.stopPropagation();
                        tts.speak(currentCard!.front, langCode || "en").catch(() => {
                          /* error surfaced via tts.error */
                        });
                        setAudioPulse(true);
                        setTimeout(() => setAudioPulse(false), 600);
                      }}
                      disabled={tts.isLoading}
                      className="w-10 h-10 rounded-full flex items-center justify-center active:scale-90 disabled:opacity-70"
                      style={{
                        background: audioPulse ? "hsl(187 72% 50% / 0.15)" : "hsl(187 72% 50% / 0.08)",
                        border: `1px solid ${audioPulse ? "hsl(187 72% 50% / 0.4)" : "hsl(187 72% 50% / 0.15)"}`,
                        boxShadow: audioPulse ? "0 0 20px hsl(187 72% 50% / 0.15)" : "none",
                      }}
                      animate={audioPulse ? { scale: [0.9, 1.1, 1] } : {}}
                      transition={{ duration: 0.4 }}>
                      {tts.isLoading ? (
                        <Loader2 size={16} className="animate-spin" style={{ color: "hsl(187 72% 55%)" }} />
                      ) : (
                        <Volume2 size={16} style={{ color: "hsl(187 72% 55%)" }} />
                      )}
                    </motion.button>
                    {tts.error && (
                      <p className="text-[11px] font-body text-foreground/60 mt-2">
                        {t("language.lessonView.audioError")}
                      </p>
                    )}
                  </div>
                </motion.div>
              </AnimatePresence>
            )}
          </div>

          {saveError && <section role="alert" className="language-status"><p>{t("language.ux.saveError")}</p>{isSpeakingPhase && <button disabled={busy} onClick={() => void completeLesson()}>{t("language.ux.retry")}</button>}</section>}
          {busy && <p role="status" className="language-status">{t("language.ux.saving")}</p>}
          {/* Next button with shimmer — hidden during Speaking phase (its own button handles the onComplete) */}
          {!isSpeakingPhase && !isPracticePhase && (
            <div className="pt-4 pb-2">
              <button disabled={busy} onClick={handleNext}
                className="w-full py-4 rounded-2xl font-display text-[15px] font-bold tracking-wide active:scale-[0.97] flex items-center justify-center gap-2 relative overflow-hidden"
                style={{ background: "hsl(187 72% 50%)", color: "#0a0a0f", boxShadow: "0 0 20px hsl(187 72% 50% / 0.2)" }}>
                {/* Shimmer sweep — GPU-only via translateX */}
                <div className="absolute inset-0 pointer-events-none overflow-hidden">
                  <div className="absolute inset-0" style={{ background: "linear-gradient(105deg, transparent 40%, rgba(255,255,255,0.12) 50%, transparent 60%)", willChange: "transform", animation: "shimmerSlide 3s ease-in-out infinite" }} />
                </div>
                <span className="relative">{t("language.lessonView.next")}</span>
                <ArrowRight size={16} className="relative" />
              </button>
            </div>
          )}

          <style>{`@keyframes shimmerSlide { 0% { transform: translateX(-100%); } 100% { transform: translateX(100%); } }`}</style>
        </div>
      </MobileLayout>
    );
  }

  /* ══ LESSONS LIST ══ */
  return (
    <MobileLayout className="language-page pb-28 pt-8 relative overflow-hidden">
      {/* Particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none" style={{ zIndex: 0 }}>
        {PARTICLES.map((p, i) => (
          <motion.div key={i} className="absolute rounded-full"
            style={{ left: p.left, top: p.top, width: p.w, height: p.w, background: p.color, opacity: 0.17, willChange: "transform, opacity" }}
            animate={reduceMotion ? undefined : { y: [0, p.yD, 0], x: [0, p.xD, 0], opacity: [0.15, 0.2, 0.15] }}
            transition={{ duration: p.dur, repeat: Infinity, ease: "easeInOut", delay: i * 0.5 }} />
        ))}
        <motion.div className="absolute w-[250px] h-[250px] rounded-full"
          style={{ bottom: -80, left: -80, background: "radial-gradient(circle, rgba(139,92,246,0.03) 0%, transparent 70%)", willChange: "transform, opacity" }}
          animate={reduceMotion ? undefined : { opacity: [0.2, 0.5, 0.2], scale: [1, 1.08, 1] }}
          transition={{ duration: 10, delay: 2, repeat: Infinity, ease: "easeInOut" }} />
      </div>

      {/* Header */}
      <button onClick={() => navigate("/language")} className="flex items-center gap-1 text-muted-foreground mb-4 active:opacity-70">
        <ChevronLeft size={16} /><span className="text-[13px] font-display">{t("common.back")}</span>
      </button>

      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="flex items-center gap-3 mb-5">
        <span className="text-[32px] leading-none">{meta.emoji}</span>
        <div>
          <h1 className="font-display text-[24px] font-bold text-foreground leading-none">{meta.label}</h1>
          {level && (
            <span className="text-[9px] font-display font-bold px-1.5 py-0.5 rounded-full mt-1 inline-block"
              style={{ background: "hsl(187 72% 50% / 0.1)", color: "hsl(187 72% 55%)", border: "1px solid hsl(187 72% 50% / 0.2)" }}>
              {level}
            </span>
          )}
        </div>
      </motion.div>

      {/* Compact Avatar */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={stagger(0)} className="mb-5">
        <AvatarTeacher compact />
      </motion.div>

      {learning.loadError && <p role="alert" className="language-status">{t("language.ux.stale")}</p>}
      {saveError && <p role="alert" className="language-status">{t("language.ux.saveError")}</p>}
      {busy && <p role="status" className="language-status">{t("language.ux.saving")}</p>}
      {lessonsError && <section role="alert" className="language-status"><p>{t("language.lessonView.loadError")}</p><button disabled={lessonsLoading} onClick={() => setLoadAttempt(value => value + 1)}>{t("language.ux.retry")}</button></section>}
      {/* Lessons */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={stagger(1)}>
        <p className="text-[9px] font-display tracking-[0.18em] uppercase mb-3" style={{ color: "hsl(187 72% 50% / 0.55)" }}>
          {t("language.lessonView.lessonsTitle")}
        </p>
        {lessonsLoading ? (
          <div className="space-y-3 mb-4">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className="rounded-2xl p-4 animate-pulse"
                style={{ background: "hsl(var(--card))", border: "1px solid hsl(187 72% 50% / 0.10)", minHeight: 88 }}
              >
                <div className="h-3 w-24 rounded mb-2" style={{ background: "hsl(187 72% 50% / 0.12)" }} />
                <div className="h-4 w-3/5 rounded mb-2" style={{ background: "hsl(187 72% 50% / 0.08)" }} />
                <div className="h-3 w-4/5 rounded" style={{ background: "hsl(187 72% 50% / 0.06)" }} />
              </div>
            ))}
          </div>
        ) : lessons.length > 0 ? (
          <LanguageJourney lessons={lessons} completed={completedLessons} language={langCode!} level={userLevel} busy={busy} onStart={lesson => void handleStartLesson(lesson)} />
        ) : (
          <div className="rounded-2xl p-6 text-center mb-4"
            style={{ background: "hsl(var(--card))", border: "1px solid hsl(187 72% 50% / 0.12)" }}>
            <button className="language-action mb-3" onClick={() => navigate(`/language/setup?lang=${langCode}`)}>{t("language.game.chooseLanguage")}</button>
            <BookOpen size={24} className="mx-auto mb-2" style={{ color: "hsl(218 14% 40%)" }} />
            <p className="text-[13px] font-body text-foreground/50">{t(profile ? "language.lessonView.comingSoon" : "language.game.chooseLanguage")}</p>
          </div>
        )}
      </motion.div>

    </MobileLayout>
  );
};

const LanguageLessonView: React.FC = () => {
  const [searchParams] = useSearchParams();
  const learning = useLanguageLearning(), { langCode } = useParams<{ langCode: string }>(), { lang } = useLanguage();
  if (!learning.userId || !learning.hasData) return <LanguageState learning={learning} />;
  const profile = learning.profiles.find(p => p.learning_language === langCode);
  if (searchParams.get('practice') === 'review' && profile) return <MotionConfig reducedMotion="user"><LanguageReview key={`${learning.userId}:${langCode}`} learning={learning} language={langCode as LangCode}/></MotionConfig>;
  return <MotionConfig reducedMotion="user"><LanguageLessonContent key={`${learning.userId}:${langCode}:${profile?.level}:${lang}`} learning={learning} /></MotionConfig>;
};
export default LanguageLessonView;
